import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import ResizeObserver from 'resize-observer-polyfill';
import axios from 'axios';
import styled, { css } from 'styled-components';
import { Button, ButtonGroup, Drawer, Icon, Menu, MenuItem } from '@blueprintjs/core';
import { Navbar, NavbarGroup, NavbarHeading, NavbarDivider, Dialog } from '@blueprintjs/core';
import { Popover2, Tooltip2 } from '@blueprintjs/popover2';
import { Select } from '@blueprintjs/select';
import { Alignment, Classes, IconSize, DrawerSize, Position, Colors, Intent } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import { Pannellum } from '$root/components/pannellum-react-local';
import TagPlaceCursor from '$public/images/tag-place-cursor_64x64.png';
import { A360Provider } from '../../contexts/a360';

import {
    has as xHas,
    isArray as xIsArray,
    isEmpty as xIsEmpty,
    findKey as xFindKey,
} from 'lodash';

import { useAuth } from '$root/contexts/auth';

import { BiGridAlt } from 'react-icons/bi';
import {
    AddTagIcon,
    EditTagIcon,
    EditTagAttachmentsIcon,
    DeleteTagIcon,
    ListTagsIcon,
    TagsLegendIcon
} from '$root/graphics/CustomIcons';

import {
    setAvailableProjects,
    setProjectSelectVisible,
    setAvailablePlans,
    setSelectedPlan,
    setSelectedViewpoint,
    setPlanViewState,
    setPlanVisibleLayers,
    setLeftYaw,
    setLeftPitch,
    setLeftHfov,
    setSplitView,
    setTagsLegendVisible,
} from '$root/store/actions';
import ProjectSelect from '$root/components/ProjectSelect';
import DarkModeSwitch from '$root/components/DarkModeSwitch';
import { SideBar } from '$root/components/SideBar';
import { SideBarButton } from '$root/components/SideBarButton';
import MultiSelectPopup from '$root/components/MultiSelectPopup';
import PlanViewFrame from '$root/components/PlanViewFrame';
import PlanViewAdvanced2 from '$root/components/planview/PlanViewAdvanced2';
import PlanViewAdvanced2ViewPointSelect from '$root/components/planview/PlanViewAdvanced2.ViewPointSelect';
import {PLAN_VIEW_VIEWPOINT_CLICKED_EVENT_TYPE} from '$root/components/planview/PlanViewAdvanced2.ViewPointSelect';
import PlanViewAdvanced2DrawViewPoints from '$root/components/planview/PlanViewAdvanced2.DrawViewPoints';
import { ViewPoint } from '$root/components/planview/PlanViewAdvanced2.ViewPoint';
import {
    PlanViewZoomExtentsEvent,
    PlanViewRegenEvent,
    PLAN_VIEW_CLICK_EVENT_TYPE,
    PLAN_VIEW_MOUSEMOVE_EVENT_TYPE, PlanViewRepaintEvent,
} from '$root/components/planview/PlanViewAdvanced2.PlanViewEvents';
import SingleDocumentView from '$root/components/SingleDocumentView';
import AutodeskModelView from '$root/components/AutodeskModelView';
import TagList from '$root/components/TagList';
import EditTagDialog, { EditTagMode } from '$components/dialogs/EditTagDialog';
import EditTagLinkDialog from '$components/dialogs/EditTagLinkDialog';
import AskAndEraseTagDialog from '$components/dialogs/AskAndEraseTagDialog';
import ShowTagHistoryDialog from '$components/dialogs/ShowTagHistoryDialog';
import { Corner, WindowState } from '$root/utils/enums';
import { style } from 'redux-logger/src/diff';

const pdfjsLib = require('pdfjs-dist/build/pdf');


const SelectorContainer = styled.div.attrs(props => ({
    visible: props.visible,
    justifyContent: props.justifyContent || 'center',
    dock: props.dock || 'top',
}))`
    visibility: ${props => props.visible ? 'visible' : 'hidden'};
    display: flex;
    flex-direction: row;
    justify-content: ${props => props.justifyContent};
    position: absolute;
    ${props => props.dock === 'top' && `
        top: 0;
    `}
    ${props => props.dock === 'bottom' && `
        bottom: 0;
    `}
    left:0;
    right:0;
    padding: 8px;
    gap: 4px;
    z-index: 18;
    
    transition: all 0.25s ease-in-out;
    
    &:hover > * {
        //opacity: 1.0;
        box-shadow: 0 0 6px 3px #606060;
    }
    & > * {
        //opacity: 0.65;
    }
`;


const TagsLegendContainer = styled.div.attrs(props => ({}))`
    display: flex;
    flex-direction: column;
    gap: 1px;
    position: absolute;
    right: 8px;
    top: 8px;
    padding: 4px;
    background-color: ${Colors.LIGHT_GRAY5};
    border: 2px solid ${Colors.LIGHT_GRAY2};
    border-radius: 3px;
    z-index: 19;
`;

const MODEL_ID = -13;

export default function MainPage(props) {

    const { getProtectedResource, keepalive, user, apiCallUrlFull } = useAuth();

    const dispatch = useDispatch();

    const availableProjects = useSelector(state => state.project.availableProjects);
    const selectedProject = useSelector(state => state.project.selectedProject);
    const availablePlans = useSelector(state => state.project.availablePlans);
    const selectedPlan = useSelector(state => state.project.selectedPlan);
    const leftYaw = useSelector(state => state.project.leftYaw);
    const leftPitch = useSelector(state => state.project.leftPitch);
    const leftHfov = useSelector(state => state.project.leftHfov);
    const splitView = useSelector(state => state.project.splitView);
    const selectedViewpointId = useSelector(state => state.project.selectedViewpointId);
    const planVisibleLayers = useSelector(state => state.project.planVisibleLayers);
    const projectSelectVisible = useSelector(state => state.ui.projectSelectVisible);

    const uiPlanView = useSelector(state => state.ui.planView);
    const uiPlanViewState = useSelector(state => state.ui.planView.state);
    const uiDarkMode = useSelector(state => state.ui.darkMode);
    const tagsLegendVisible = useSelector(state => state.ui.tagsLegendVisible);

    const runtimeSettingsRef = useRef({
        skipDocumentSelectOnPlanChange:false
    });
    const resizeObserverRef = useRef(null);

    const renderCnt = useRef(0);
    const planViewRef = useRef();
    const planViewViewPointDrawerRef = useRef();
    const planViewViewPointSelectRef = useRef();

    const leftPannellumRef = useRef();
    const rightPannellumRef = useRef();
    const suspendSyncRef = useRef(false);

    const leftYawRef = useRef(leftYaw);
    const leftPitchRef = useRef(leftPitch);
    const leftHfovRef = useRef(leftHfov);

    const tagCanvasContainerRef = useRef();
    const tagCanvasJigRef = useRef();
    const tagCanvasRef = useRef();
    const tagToolsRef = useRef();
    const tagRuntime = useRef({
        suspendMouseEvents: false,
        tagPathCache:{},
    });

    const [ planDocument, setPlanDocument ] = useState({
        pdfDocument:null,
        pdfPage:null,
        pdfPageViewport:null,
        pdfOptionalContentConfig:null,
        pdfViewPointTrafo:[1,0,0,1,0,0],
        pdfViewPoints:[],
        pdfGroups:[],
    });

    const [ panoDocument, setPanoDocument ] = useState({
        viewPointDocuments:[],
        leftDocumentId:-1,
        rightDocumentId:-1,
        hotSpots:[]
    });

    const [ leftDocument, setLeftDocument ] = useState({
        id:-1,
        url:"",
        tags:[]
    });
    const [ reloadTags, setReloadTags ] = useState(false);
    const leftTagsRef = useRef(leftDocument.tags);
    const [ tagSettings, setTagSettings ] = useState({
        categories: [],
        disciplines: [],
        statuses: [],
    });

    const [ rightDocument, setRightDocument ] = useState({
        id:-1,
        url:"",
        tags:[]
    });
    const [ showTagHistory, setShowTagHistory ] = useState({
        id:-1,
        show:false
    });
    const [ showPlanTagList, setShowPlanTagList ] = useState({
        show:false,
    });
    const [ showTagHistoryPhoto, setShowTagHistoryPhoto ] = useState({
        show:false,
        url:null,
    });

    const TAG_INACTIVE = -1;
    const TAG_PICK_TARGET = 0;
    const TAG_EDIT_TAG = 1;
    const TAG_ASK_ERASE_TAG = 2;

    const [ tagAddState, setTagAddState ] = useState({
        step:TAG_INACTIVE,
    });

    const [ tagEditState, setTagEditState ] = useState({
        step:TAG_INACTIVE,
        tagId:-1,
    });

    const [ tagEraseState, setTagEraseState ] = useState({
        step:TAG_INACTIVE,
        tagId:-1,
    });

    const [ tagLinkEditState, setTagLinkEditState ] = useState({
        step:TAG_INACTIVE,
        tagId:-1,
    });

    const tagJig = useRef({
        yaw:0,
        pitch:0,
        orientation:0,
        orientationIncrement:45 * Math.PI / 180
    });


    useEffect(() => {
        if (availableProjects) {
            return;
        }

        getProtectedResource(`/dashboard.php?task=projects`)
            .then(response => {
                if (!xHas(response, 'data.data') ||
                    !xIsArray(response.data.data) ||
                    response.data.data.length === 0) {
                //     logout();
                //     AppToaster.show({
                //         message:'** There are no properly configured projects.',
                //         intent:Intent.DANGER
                //     });
                }
                else {
                    dispatch(setAvailableProjects(response.data.data));
                //     setProjects(response.data.data);
                //     if (response.data.data.findIndex(p => p.id === selectedProjectId) < 0)
                //         selectedProjectId(0);
                //     setSelectedProjectPlans(null);
                //     dashboardDataRef.current.projectPlans = [];
                }
            })
            .catch(err => {
                console.error(err)
            })
        ;

    }, [availableProjects]);


    useEffect(() => {

        if (!selectedProject) {
            return;
        }

        getProtectedResource(`/dashboard.php?task=plans&project=${selectedProject.id}`)
            .then(response => {
                if (!xHas(response, 'data.data') ||
                    !xIsArray(response.data.data) ||
                    response.data.data.length === 0) {
                    //     logout();
                    //     AppToaster.show({
                    //         message:'** There are no properly configured projects.',
                    //         intent:Intent.DANGER
                    //     });
                }
                else {
                    dispatch(setAvailablePlans(response.data.data));
                }
            })
            .catch(err => {
                console.error(err)
            })
        ;

    }, [selectedProject]);


    useEffect(() => {
        if (!selectedProject) return;
        if (!availablePlans) return;

        let plan = (availablePlans||[]).find(p => p.id === selectedPlan?.id);
        if (!plan && availablePlans?.length) {
            plan = availablePlans[0];
        }
        if (selectedPlan?.id !== plan?.id) {
            dispatch(setSelectedPlan(plan));
            setPanoDocument(old => ({
                ...old,
                viewPointDocuments:[],
                leftDocumentId:-1,
                rightDocumentId:-1,
                hotSpots:[]
            }));
            setLeftDocument(old => ({
                ...old,
                id:-1,
                url:'',
                tags:[]
            }));
            setRightDocument(old => ({
                ...old,
                id:-1,
                url:'',
                tags:[]
            }));
        }
    }, [selectedProject, availablePlans, selectedPlan, setPanoDocument, setLeftDocument, setRightDocument]);


    useEffect(() => {

        if (!selectedPlan)
            return;

        Promise.all([
            getProtectedResource(`/document.php?content&id=${selectedPlan.id}`, 'arraybuffer'),
            getProtectedResource(`/viewpoint.php?vplistof&did=${selectedPlan.id}`),
        ])
            .then(results => {
                const [ documentContent, viewpoints ] = results;

                const contentOk =  (documentContent && documentContent.data);

                if (!contentOk)
                    return ;

                const loadingTask = pdfjsLib.getDocument(documentContent.data);

                loadingTask.promise
                    .then(async function (pdfDocument) {

                        const optionals = await pdfDocument.getOptionalContentConfig();

                        return pdfDocument.getPage(selectedPlan.page_index).then(function (page) {
                            const pageViewport = page.getViewport({scale:1,offsetX:0,offsetY:0});
                            const vpTr = new DOMMatrix(selectedPlan.viewpoint_trafo);
                            let groups = [];
                            if (optionals) {
                                const order = optionals.getOrder();
                                if (order && order.length) {
                                    groups = order.map(id => ({
                                        id:id,
                                        name:optionals.getGroup(id).name,
                                    }));
                                }
                            }

                            setPlanDocument(old => ({
                                ...old,
                                pdfDocument:pdfDocument,
                                pdfPage:page,
                                pdfPageViewport:pageViewport,
                                pdfOptionalContentConfig:optionals,
                                pdfViewPointTrafo:vpTr,
                                pdfViewPoints:(selectedPlan.viewpoints || []).map(v => {
                                    const vp = (new DOMPoint(v.x, v.y)).matrixTransform(vpTr);
                                    vp.id = v.id;
                                    vp.name = v.name;
                                    vp.has_tags = v.has_tags ?? false;

                                    const dbvp = viewpoints.data.data.find(v => v.id === vp.id);
                                    if (dbvp)
                                        vp.has_tags = dbvp.has_tags ?? false;

                                    return ViewPoint.make(vp);
                                }),
                                pdfGroups:groups
                            }));
                            dispatch(setPlanVisibleLayers(groups.map(g => g.id)));
                        });
                    })
                    .catch(function (reason) {
                        console.error("Error: " + reason);
                    });
            })
            .catch(err => console.error(err))
        ;

    }, [selectedPlan, setPlanDocument]);


    useEffect(() => {
        dispatch(setSelectedViewpoint(-1));
    }, [selectedPlan]);


    useEffect(() => {

        if (selectedViewpointId <= 0)
            return;

        getProtectedResource(`/viewer.php?documentsat&vpid=${selectedViewpointId}`, 'json')
            .then(documents => {
                const viewpointDocs = xIsArray(documents.data.data) ? documents.data.data : [];
                viewpointDocs.sort((a, b) => {
                    const da = new Date(a.upload_at);
                    const db = new Date(b.upload_at);
                    return da < db ? 1 : (db < da ? -1 : 0);
                });
                console.log(selectedPlan)
                console.log(viewpointDocs)

                let hasModel = false;
                if (viewpointDocs.length > 0 &&
                    selectedPlan?.a360_hub?.length > 0 &&
                    selectedPlan?.a360_file?.length > 0 &&
                    selectedPlan?.a360_project?.length > 0)
                {
                    viewpointDocs.push({
                        "category": "Modell",
                        "category_id": null,
                        "direction": null,
                        "id": MODEL_ID,
                        "name": "Modell",
                        "navigation_radius": null,
                        "original_name": null,
                        "page_index": null,
                        "project_id": "1",
                        "type": "model/gltf-binary",
                        "upload_at": null,
                        "viewpoint": null,
                        "viewpoint_id": null,
                        "viewpoint_trafo": null,
                    });
                    hasModel = true;
                }

                let leftId = -1;
                let rightId = -1;

                if (!runtimeSettingsRef.current.skipDocumentSelectOnPlanChange) {
                    if (viewpointDocs.length > 1) {
                        if (splitView) {
                            leftId = viewpointDocs[1].id;
                            rightId = viewpointDocs[0].id;
                        } else {
                            leftId = viewpointDocs[0].id;
                        }
                    } else if (viewpointDocs.length > 0) {
                        leftId = viewpointDocs[0].id;
                    }
                }

                //
                let hotSpots = [];
                const selectedVP = selectedPlan.viewpoints.find(vp => vp.id === selectedViewpointId);
                const nearBy = selectedPlan.viewpoints.filter(vp => {
                    if (!vp || !selectedVP)
                        return false;
                    if (vp.id === selectedVP.id)
                        return false;
                    const dx = selectedVP.x - vp.x;
                    const dy = selectedVP.y - vp.y;
                    return Math.sqrt(dx * dx + dy * dy) <= selectedPlan.navigation_radius;
                });
                nearBy.sort((vpl, vpr) => vpl.name.toLowerCase() < vpr.name.toLowerCase() ? -1 :
                    vpl.name.toLowerCase() > vpr.name.toLowerCase() ? 1 : 0);
                hotSpots = nearBy.map(vp => {
                    const dx = vp.x - selectedVP.x;
                    const dy = vp.y - selectedVP.y;
                    const dz = -0.5;
                    const l = Math.sqrt(dx * dx + dy * dy);
                    const ya = Math.PI / 2.0 - Math.atan2(dy, dx);
                    //const ya = Math.PI - (Math.atan2(dy, dx) + Math.PI / 2.0);
                    const pi = Math.atan2(dz, l);
                    return {
                        pitch:pi * 180.0 / Math.PI,
                        yaw:(ya - selectedPlan.direction) * 180.0 / Math.PI,
                        id:vp.id,
                        text:vp.name,
                        type:''
                    };
                });

                if (hasModel && panoDocument.leftDocumentId === MODEL_ID)
                {
                    leftId = MODEL_ID;
                }
                if (runtimeSettingsRef.current.skipDocumentSelectOnPlanChange) {
                    setPanoDocument(old => ({
                        ...old,
                        viewPointDocuments:viewpointDocs,
                        hotSpots:hotSpots
                    }));
                }
                else {
                    setPanoDocument(old => ({
                        ...old,
                        viewPointDocuments:viewpointDocs,
                        leftDocumentId:leftId,
                        rightDocumentId:rightId,
                        hotSpots:hotSpots
                    }));
                }

                runtimeSettingsRef.current.skipDocumentSelectOnPlanChange = false;

                if (splitView && rightId === -1)
                {
                    dispatch(setSplitView(false));
                }
            })
            .catch(err => {
                console.error(err)
            });
    }, [selectedViewpointId, splitView, setPanoDocument]);


    useEffect(() => {
        if (leftDocument.id === panoDocument.leftDocumentId)
            return;

        Promise.all([
            getProtectedResource(`/viewer.php?content&did=${panoDocument.leftDocumentId}`, 'arraybuffer'),
            ])
            .then(results => {
                const [pano] = results;

                const blob = new Blob([ pano.data ]);
                const url = URL.createObjectURL(blob);
                setLeftDocument(old => ({
                    ...old,
                    id:panoDocument.leftDocumentId,
                    url: url,
                }));
            })
            .catch(err => {
                console.error(err)
            })
        ;
    }, [panoDocument, leftDocument, setLeftDocument, setTagSettings]);


    useEffect(() => {
        if (!selectedProject)
            return;

        Promise.all([
            getProtectedResource(`/tag2.php?task=tag-by-document&did=${panoDocument.leftDocumentId}`),
            getProtectedResource(`/tag2.php?task=tag-settings&pid=${selectedProject.id}`)
            ])
            .then(results => {
                const [tags, settings] = results;

                const { categories, disciplines, statuses } = settings.data.settings;

                leftTagsRef.current = tags?.data?.tags ?? [];

                setLeftDocument(old => ({
                    ...old,
                    tags: leftTagsRef.current
                }));
                setTagSettings(old => ({
                    ...old,
                    categories:categories,
                    disciplines:disciplines,
                    statuses:statuses,
                }));
            })
            .catch(err => {
                console.error(err)
            })
        ;
    }, [panoDocument, reloadTags, setLeftDocument]);


    useEffect(() => {
        if (rightDocument.id === panoDocument.rightDocumentId)
            return;

        getProtectedResource(`/viewer.php?content&did=${panoDocument.rightDocumentId}`, 'arraybuffer')
            .then(content => {
                const blob = new Blob([ content.data ]);
                const url = URL.createObjectURL(blob);
                setRightDocument(old => ({
                    ...old,
                    id:panoDocument.rightDocumentId,
                    url: url
                }));
            })
            .catch(err => {
                console.error(err)
            })
        ;
    }, [panoDocument, rightDocument, setRightDocument]);


    const onZoomExtents = useCallback(() => {
        keepalive();
        if (planViewRef.current && planDocument.pdfPage)
        {
            planViewRef.current.dispatchEvent(new PlanViewZoomExtentsEvent());
        }
    }, [ uiPlanViewState, planDocument]);


    const onRegen = useCallback(() => {
        keepalive();
        if (planViewRef.current && planDocument.pdfPage)
        {
            planViewRef.current.dispatchEvent(new PlanViewRegenEvent());
        }
    }, [ uiPlanViewState, planDocument]);


    const onKeyDown = useCallback((evt) => {
        const { key, altKey, ctrlKey, shiftKey } = evt;

        if (altKey && !ctrlKey && !shiftKey) {
            if (key === 'x' || key === 'X') {
                onZoomExtents();
                return;
            }
        }
        if (altKey && !ctrlKey && !shiftKey) {
            if (key === 'r' || key === 'R') {
                onRegen();
                return;
            }
        }
        if (!altKey && !ctrlKey && !shiftKey) {
            if (key === 'Escape' && uiPlanViewState === WindowState.MAXIMIZED) {
                keepalive();
                dispatch(setPlanViewState(WindowState.NORMAL));
                return;
            }
        }
    }, [ uiPlanViewState, onZoomExtents, onRegen]);


    useEffect(() => {

        window.addEventListener('keydown', onKeyDown);

        return () => {
            window.removeEventListener('keydown', onKeyDown);
        }
    }, [onKeyDown]);


    const onViewpointClicked = useCallback((e) => {
        dispatch(setSelectedViewpoint(e.viewPointId));
        if (uiPlanViewState === WindowState.MAXIMIZED)
        {
            dispatch(setPlanViewState(WindowState.NORMAL));
        }
    }, [uiPlanViewState]);


    useEffect(() => {
        if (!(planViewRef?.current instanceof HTMLElement))
            return;

        planViewRef.current.addEventListener(PLAN_VIEW_VIEWPOINT_CLICKED_EVENT_TYPE, onViewpointClicked);

        return () => {
            planViewRef.current?.removeEventListener(PLAN_VIEW_VIEWPOINT_CLICKED_EVENT_TYPE, onViewpointClicked);
        };

    }, [planViewRef.current, onViewpointClicked]);


    const onSetYaw = (newYaw) => {

        leftYawRef.current = newYaw;

        leftPannellumRef.current?.getViewer().setYaw(leftYawRef.current, false);
        rightPannellumRef.current?.getViewer().setYaw(leftYawRef.current, false);

        dispatch(setLeftYaw(leftYawRef.current));
    };


    const onSetPitch = (newPitch) => {

        leftPitchRef.current = newPitch;

        leftPannellumRef.current?.getViewer().setPitch(leftPitchRef.current, false);
        rightPannellumRef.current?.getViewer().setPitch(leftPitchRef.current, false);

        dispatch(setLeftPitch(leftPitchRef.current));
    };


    const onSetHfov = (newHfov) => {

        leftHfovRef.current = newHfov;

        leftPannellumRef.current?.getViewer().setHfov(leftHfovRef.current, false);
        rightPannellumRef.current?.getViewer().setHfov(leftHfovRef.current, false);

        dispatch(setLeftHfov(leftHfovRef.current));
    };


    const onLeftPanoRender = useCallback((tags) => {
        if (suspendSyncRef.current)
            return;

        suspendSyncRef.current = true;

        const panoViewer = leftPannellumRef.current?.getViewer();

        if (panoViewer) {
            onSetYaw(panoViewer.getYaw());
            onSetPitch(panoViewer.getPitch());
            onSetHfov(panoViewer.getHfov());
        }

        tagRuntime.current.tagPathCache = {};
        if (panoViewer && tagCanvasRef.current) {
            drawTags(panoViewer, tags);
        }

        suspendSyncRef.current = false;
    }, [onSetYaw, onSetPitch, onSetHfov]);


    const onRightPanoRender = useCallback(() => {
        if (suspendSyncRef.current)
            return;

        suspendSyncRef.current = true;

        const panoViewer = rightPannellumRef.current?.getViewer();

        if (panoViewer) {
            onSetYaw(panoViewer.getYaw());
            onSetPitch(panoViewer.getPitch());
            onSetHfov(panoViewer.getHfov());
        }

        suspendSyncRef.current = false;
    }, [onSetYaw, onSetPitch, onSetHfov]);


    useEffect(() => {
        leftPannellumRef.current?.getViewer()?.resize();
        leftPannellumRef.current?.getViewer()?.getRenderer()?.resize();
    }, [leftDocument]);


    useEffect(() => {
        if (tagCanvasContainerRef.current == null)
            return;
        if (showTagHistory.show)
            return;
        if (tagAddState.step !== TAG_INACTIVE)
            return;
        if (tagEditState.step !== TAG_INACTIVE)
            return;
        if (tagLinkEditState.step !== TAG_INACTIVE)
            return;
        if (tagEraseState.step !== TAG_INACTIVE)
            return;

        resizeObserverRef.current = new ResizeObserver(entries => {
            if (entries?.length > 0)
            {
                const w = Math.ceil(entries[0].contentRect.width);
                const h = Math.ceil(entries[0].contentRect.height);

                if (tagCanvasJigRef.current) {
                    tagCanvasJigRef.current.width = w;
                    tagCanvasJigRef.current.height = h;
                }
                if (tagCanvasRef.current) {
                    tagCanvasRef.current.width = w;
                    tagCanvasRef.current.height = h;
                }
            }
        });
        resizeObserverRef.current.observe(tagCanvasContainerRef.current);

        const onMouseClick = (evt) => {

            if (tagRuntime.current.suspendMouseEvents)
                return;
            if (tagCanvasContainerRef.current == null)
                return;

            const rc = tagCanvasContainerRef.current.getBoundingClientRect();

            const mPos = {
                x:evt.clientX - rc.left,
                y:evt.clientY - rc.top,
            };
            if (mPos.x < 0 || mPos.x > rc.width || mPos.y < 0 || mPos.y > rc.height) {
                return;
            }
            const canvas = tagCanvasRef.current;
            const ctx = canvas.getContext("2d");

            const checkMousePos = (entry) => ctx.isPointInPath(entry.path, mPos.x, mPos.y);
            const tagKey = xFindKey(tagRuntime.current.tagPathCache, checkMousePos);

            if (tagKey) {
                evt.preventDefault();
                evt.stopPropagation();

                keepalive();

                setShowTagHistory({id:1*tagKey, show:true});
            }
        }

        window.addEventListener('click', onMouseClick);

        return () => {
            resizeObserverRef.current?.disconnect();
            resizeObserverRef.current = null;

            window.removeEventListener('click', onMouseClick);
        }

    }, [tagCanvasContainerRef.current, tagAddState, tagEditState, tagLinkEditState, tagEraseState, setShowTagHistory]);


    const suspendTagMouseEventHandling = () => {
        tagRuntime.current.suspendMouseEvents = true;
    }


    const restoreTagMouseEventHandling = () => {
        tagRuntime.current.suspendMouseEvents = false;
    }


    useEffect(() => {
        const interaction = evt => {

            if (!leftPannellumRef.current)
                return;

            if (evt instanceof KeyboardEvent) {
                if (evt.type === 'keydown') {
                    if (evt.code === "ArrowLeft" || evt.code === "ArrowRight") {
                        evt.preventDefault();

                        const d = evt.code === "ArrowLeft" ? -1.5 : 1.5;

                        let y = leftYawRef.current + d;
                        if (y <= -180)
                            y = 360 + y;
                        else if (y > 180)
                            y = y - 360;

                        onSetYaw(y);
                    }
                    else if (evt.code === "ArrowUp" || evt.code === "ArrowDown") {
                        evt.preventDefault();

                        const d = evt.code === "ArrowDown" ? -1.5 : 1.5;

                        let tpitch = leftPitchRef.current + d;
                        if (tpitch <= -70)
                            tpitch = 70;
                        else if (tpitch > 70)
                            tpitch = 70;

                        onSetPitch(tpitch);
                    }
                }
            }
        }

        window.addEventListener('keydown', interaction);
        window.addEventListener('keyup', interaction);

        return () => {
            window.removeEventListener('keydown', interaction);
            window.removeEventListener('keyup', interaction);
        }

    }, []);


    const propagateHotSpots = useCallback((panellumRef, hotSpots) => {
        const viewer = panellumRef.getViewer();

        hotSpots.forEach(hs => {
            let className = "pano-hotspot";

            viewer.addHotSpot({
                ...hs,
                cssClass:`${className} pnlm-sprite`,
                clickHandlerFunc: () => dispatch(setSelectedViewpoint(hs.id))
            });
        });
    }, []);


    const onShowHistoryImage = useCallback((hid) => {
        getProtectedResource(`/tag2.php?task=tag-photo-by-history&thid=${hid}`, 'arraybuffer')
            .then(response => {
                const blob = new Blob([ response.data ]);
                if (blob.size) {
                    const url = URL.createObjectURL(blob);

                    setShowTagHistoryPhoto({
                        show:true,
                        url
                    });
                }
            })
            .catch((err) => {
                console.error(err)
            })
        ;
    }, [setShowTagHistoryPhoto]);


    const onJumpToTag = useCallback((plan_id, document_id, viewpoint_id, yaw, pitch) => {
        const plan = (availablePlans||[]).find(p => p.id == plan_id);
        if (plan) {
            runtimeSettingsRef.current.skipDocumentSelectOnPlanChange = true;

            dispatch(setSelectedPlan(plan));
            setPanoDocument(old => ({
                ...old,
                leftDocumentId:document_id,
                hotSpots:[]
            }));
            dispatch(setSelectedViewpoint(viewpoint_id));
            onSetYaw(yaw*180/Math.PI);
            onSetPitch(pitch*180/Math.PI);
        }

        setShowPlanTagList({ show:false });

    }, [availablePlans, setShowPlanTagList, setPanoDocument, onSetYaw, onSetPitch]);


    //<editor-fold desc="Tag">

    const tagSize = 56;


    const outlineTemplatePoints = [
        /*  0 */ { x:0.000000, y:0.000000 },
        /*  1 */ { x:0.037442, y:0.024962 },
        /*  2 */ { x:0.237404, y:0.158269 },
        /*  3 */ { x:0.250000, y:0.166667 },
        /*  4 */ { x:0.265139, y:0.166667 },
        /*  5 */ { x:0.970000, y:0.166667 },
        /*  6 */ { x:1.000000, y:0.166667 },
        /*  7 */ { x:1.000000, y:0.136667 },
        /*  8 */ { x:1.000000, y:-0.136667 },
        /*  9 */ { x:1.000000, y:-0.166667 },
        /* 10 */ { x:0.970000, y:-0.166667 },
        /* 11 */ { x:0.265139, y:-0.166667 },
        /* 12 */ { x:0.250000, y:-0.166667 },
        /* 13 */ { x:0.237404, y:-0.158269 },
        /* 14 */ { x:0.037442, y:-0.024962 },
        /* 15 */ { x:0.15, y:0 },
        /* 16 */ { x:0.19, y:0 },
        /* 17 */ { x:0.26, y:0.0 }, // text center
    ];


    const outlineTemplatePointsSecondHalf = [
        /*  0 */ { x:0.600000, y:0.166667 },
        /*  1 */ { x:0.970000, y:0.166667 },
        /*  2 */ { x:1.000000, y:0.166667 },
        /*  3 */ { x:1.000000, y:0.136667 },
        /*  4 */ { x:1.000000, y:-0.136667 },
        /*  5 */ { x:1.000000, y:-0.166667 },
        /*  6 */ { x:0.970000, y:-0.166667 },
        /*  7 */ { x:0.600000, y:-0.166667 },
    ];


    const yawPitchToPixelCoord = (yaw, pitch, viewInfo) => {
        const { viewPitch, viewYaw, viewHfov, canvasW, canvasH } = viewInfo;
        const
            hsPitchSin = Math.sin(pitch * Math.PI / 180),
            hsPitchCos = Math.cos(pitch * Math.PI / 180),
            configPitchSin = Math.sin(viewPitch * Math.PI / 180),
            configPitchCos = Math.cos(viewPitch * Math.PI / 180),
            yawCos = Math.cos((-yaw + viewYaw) * Math.PI / 180)
        ;
        const z = hsPitchSin * configPitchSin + hsPitchCos * yawCos * configPitchCos;

        if ((yaw <= 90 && yaw > -90 && z <= 0) ||
            ((yaw > 90 || yaw <= -90) && z <= 0)) {
            return null;
        }

        const
            yawSin = Math.sin((-yaw + viewYaw) * Math.PI / 180),
            hfovTan = Math.tan(viewHfov * Math.PI / 360)
        ;

        let coord = [
            -canvasW / hfovTan * yawSin * hsPitchCos / z / 2,
            -canvasW / hfovTan * (hsPitchSin * configPitchCos - hsPitchCos * yawCos * configPitchSin) / z / 2
        ];

        // Apply roll
        const
            rollSin = 0, //Math.sin(0 * Math.PI / 180),
            rollCos = 1  //Math.cos(0 * Math.PI / 180);
        ;
        coord = [
            coord[0] * rollCos - coord[1] * rollSin,
            coord[0] * rollSin + coord[1] * rollCos
        ];

        // Apply transform
        coord[0] += (canvasW - 0) / 2;
        coord[1] += (canvasH - 0) / 2;

        return { x:coord[0], y:coord[1] };
    }


    const createTagPath2DAt = (yaw, pitch, orientation, tagSize, viewInfo, adjust) => {
        const coord = yawPitchToPixelCoord(yaw * 180 / Math.PI, pitch * 180 / Math.PI, viewInfo);
        if (!coord) return null;

        const tagAngle = orientation;
        const sinTagAngle = Math.sin(tagAngle);
        const cosTagAngle = Math.cos(tagAngle);

        adjust = adjust ?? 0;

        const outlinePoints = outlineTemplatePoints.map((p,i) => {
            let dx = 0;
            if (i >= 5 && i <= 10)
                dx = adjust;

            return {
                x:(p.x+dx) * cosTagAngle - p.y * sinTagAngle,
                y:(p.x+dx) * sinTagAngle + p.y * cosTagAngle
            };
        });
        const outlinePointsSecondHalf = outlineTemplatePointsSecondHalf.map((p,i) => {
            let dx = (i === 0 || i === outlineTemplatePointsSecondHalf.length - 1) ? adjust * 0.5 : adjust;

            return {
                x:(p.x+dx) * cosTagAngle - p.y * sinTagAngle,
                y:(p.x+dx) * sinTagAngle + p.y * cosTagAngle
            };
        });

        const cx = x => coord.x + tagSize * x;
        const cy = y => coord.y + tagSize * y;

        const result = new Path2D();

        result.moveTo(cx(outlinePoints[1].x), cy(outlinePoints[1].y));
        result.lineTo(cx(outlinePoints[2].x), cy(outlinePoints[2].y));
        result.arcTo(cx(outlinePoints[3].x), cy(outlinePoints[3].y),
            cx(outlinePoints[4].x), cy(outlinePoints[4].y), tagSize * 0.05);

        result.lineTo(cx(outlinePoints[5].x), cy(outlinePoints[5].y));
        result.arcTo(cx(outlinePoints[6].x), cy(outlinePoints[6].y),
            cx(outlinePoints[7].x), cy(outlinePoints[7].y), tagSize * 0.05);
        result.lineTo(cx(outlinePoints[8].x), cy(outlinePoints[8].y));
        result.arcTo(cx(outlinePoints[9].x), cy(outlinePoints[9].y),
            cx(outlinePoints[10].x), cy(outlinePoints[10].y), tagSize * 0.05);

        result.lineTo(cx(outlinePoints[11].x), cy(outlinePoints[11].y));
        result.arcTo(cx(outlinePoints[12].x), cy(outlinePoints[12].y),
            cx(outlinePoints[13].x), cy(outlinePoints[13].y), tagSize * 0.05);
        result.lineTo(cx(outlinePoints[14].x), cy(outlinePoints[14].y));
        result.arcTo(cx(outlinePoints[0].x), cy(outlinePoints[0].y),
            cx(outlinePoints[1].x), cy(outlinePoints[1].y), tagSize * 0.03);
        result.closePath();

        result.moveTo(cx(outlinePoints[16].x), cy(outlinePoints[16].y));
        result.ellipse(cx(outlinePoints[15].x), cy(outlinePoints[15].y),
            tagSize * 0.04, tagSize * 0.04,
            0,
            0, 2 * Math.PI
        );


        const resultSecondHalf = new Path2D();

        resultSecondHalf.moveTo(cx(outlinePointsSecondHalf[0].x), cy(outlinePointsSecondHalf[0].y));
        resultSecondHalf.lineTo(cx(outlinePointsSecondHalf[1].x), cy(outlinePointsSecondHalf[1].y));

        resultSecondHalf.arcTo(cx(outlinePointsSecondHalf[2].x), cy(outlinePointsSecondHalf[2].y),
            cx(outlinePointsSecondHalf[3].x), cy(outlinePointsSecondHalf[3].y), tagSize * 0.05);
        resultSecondHalf.lineTo(cx(outlinePointsSecondHalf[4].x), cy(outlinePointsSecondHalf[4].y));
        resultSecondHalf.arcTo(cx(outlinePointsSecondHalf[5].x), cy(outlinePointsSecondHalf[5].y),
            cx(outlinePointsSecondHalf[6].x), cy(outlinePointsSecondHalf[6].y), tagSize * 0.05);

        resultSecondHalf.lineTo(cx(outlinePointsSecondHalf[7].x), cy(outlinePointsSecondHalf[7].y));

        return {
            path:result,
            pathSecondHalf:resultSecondHalf,
            tagCenter:{ x:cx(outlinePoints[17].x), y:cy(outlinePoints[17].y) }
        };
    }


    const drawTags = (panoViewer, tags) => {
        tagRuntime.current.tagPathCache = {};
        if (tagCanvasRef.current) {
            const canvas = tagCanvasRef.current;
            const viewInfo = {
                viewPitch:panoViewer.getPitch(),
                viewYaw:panoViewer.getYaw(),
                viewHfov:panoViewer.getHfov(),
                canvasW:canvas.width,
                canvasH:canvas.height
            };

            const ctx = canvas.getContext("2d");
            ctx.clearRect(0, 0, canvas.width, canvas.height);

            ctx.textBaseline = 'middle';
            ctx.textAlign = 'left';
            ctx.font = '12px "Roboto Condensed",sans-serif';

            const bw = 0.67 * tagSize;

            for (const tag of (tags ?? []))
            {
                const tagText = tag.tag;

                let o = tag.orientation;
                while (o < 0) o += 2 * Math.PI;
                //const tagText = `${o*180/Math.PI}`;

                const tm = ctx.measureText(tagText)
                let adjust = 0;
                if (tm.width > bw)
                {
                    adjust = (tm.width - bw) / tagSize;
                }

                const tagPath = createTagPath2DAt(
                    tag.yaw,
                    tag.pitch,
                    tag.orientation,
                    tagSize,
                    viewInfo,
                    adjust
                );

                if (tagPath) {

                    ctx.fillStyle = tag.category_bgcolor;
                    ctx.strokeStyle = tag.category_fgcolor;
                    ctx.fill(tagPath.path);

                    ctx.fillStyle = tag.discipline_bgcolor;
                    ctx.fill(tagPath.pathSecondHalf);

                    ctx.stroke(tagPath.path);

                    ctx.fillStyle = 'black';

                    ctx.textAlign = 'left';
                    if (o > Math.PI / 2 && o <= 3 * Math.PI / 2) {
                        o += Math.PI;
                        ctx.textAlign = 'right';
                    }

                    ctx.save();
                    ctx.translate(tagPath.tagCenter.x,
                        tagPath.tagCenter.y);
                    ctx.rotate(o);
                    ctx.fillText(tagText,
                        0,
                        0);
                    ctx.restore();

                    tagRuntime.current.tagPathCache[tag.id] = tagPath;
                }
            }
        }

    }


    const onTagAddStart = useCallback(() => {

        keepalive();

        setTagAddState(old => ({...old, step:TAG_PICK_TARGET}));

    }, [setTagAddState]);


    const onTagEditCloseAddTag = useCallback((reload) => {
        setTagAddState(old => ({ ...old, step:TAG_INACTIVE }))
        if (reload)
            setReloadTags(old => !old);
    }, [setTagAddState, setReloadTags]);


    const onShowPlanTags = useCallback(() => {

        keepalive();

        setShowPlanTagList(old => ({...old, show:true}));

    }, [setShowPlanTagList]);


    const onTagEditStart = useCallback(() => {

        keepalive();

        if (tagEditState.step === TAG_INACTIVE) {
            setTagEditState(old => ({ ...old, step:TAG_PICK_TARGET }));
        }
        else if (tagEditState.step === TAG_PICK_TARGET) {
            setTagEditState(old => ({ ...old, step:TAG_INACTIVE }));
        }

    }, [tagEditState, setTagEditState]);


    const onTagEditCloseEditTag = useCallback((reload) => {
        setTagEditState(old => ({ ...old, step:TAG_INACTIVE, tagId:-1}))
        if (reload)
            setReloadTags(old => !old);
    }, [setTagEditState, setReloadTags]);


    const onTagLinkEditStart = useCallback(() => {

        keepalive();

        if (tagLinkEditState.step === TAG_INACTIVE) {
            setTagLinkEditState(old => ({ ...old, step:TAG_PICK_TARGET }));
        }
        else if (tagLinkEditState.step === TAG_PICK_TARGET) {
            setTagLinkEditState(old => ({ ...old, step:TAG_INACTIVE }));
        }

    }, [tagLinkEditState, setTagLinkEditState]);


    const onTagLinkEditCloseEditTagLink = useCallback((reload) => {
        setTagLinkEditState(old => ({ ...old, step:TAG_INACTIVE, tagId:-1}))
        if (reload)
            setReloadTags(old => !old);
    }, [setTagLinkEditState, setReloadTags]);


    const onTagEraseStart = useCallback(() => {

        keepalive();

        if (tagEraseState.step === TAG_INACTIVE) {
            setTagEraseState(old => ({ ...old, step:TAG_PICK_TARGET }));
        }
        else if (tagEraseState.step === TAG_PICK_TARGET) {
            setTagEraseState(old => ({ ...old, step:TAG_INACTIVE }));
        }

    }, [tagEraseState, setTagEraseState]);


    const onTagEditCloseEraseTag = useCallback((reload) => {
        setTagEraseState(old => ({ ...old, step:TAG_INACTIVE, tagId:-1}))
        if (reload)
            setReloadTags(old => !old);
    }, [setTagEraseState, setReloadTags]);


    const onDownloadTags = useCallback((reload) => {
        const url = apiCallUrlFull(`/tag2.php?task=tag-by-project&pid=${selectedProject.id}&download`);

        window.open(url, "_blank");
    }, [selectedProject]);


    useEffect(() => {

        if (tagAddState.step !== TAG_PICK_TARGET)
            return;

        const onTagKeyDown = (evt) => {
            const { key, altKey, ctrlKey, shiftKey } = evt;
            if (!altKey && !ctrlKey && !shiftKey) {
                if (key === 'Escape') {
                    clearTagJig();
                    setTagAddState(old => ({...old, step:TAG_INACTIVE}));
                    return;
                }
            }
        };

        const onTagMouseClick = (evt) => {

            const rcC = tagCanvasContainerRef.current.getBoundingClientRect();
            const rcT = tagToolsRef.current?.getBoundingClientRect() ?? new DOMRect(0,0,0,0);

            const mPos = {
                x:evt.clientX - rcC.left,
                y:evt.clientY - rcC.top,
            };

            if (mPos.x >= rcT.left && mPos.x <= rcT.right &&
                mPos.y >= rcT.top && mPos.y <= rcT.bottom) {
                return;
            }
            if (mPos.x < rcC.left || mPos.x > rcC.right ||
                mPos.y < rcC.top || mPos.y > rcC.bottom) {
                return;
            }

            evt.preventDefault();
            evt.stopPropagation();

            clearTagJig();

            setTagAddState(old => ({...old, step:TAG_EDIT_TAG}));
        };

        const onTagMouseMove = (evt) => {

            const rc = tagCanvasContainerRef.current.getBoundingClientRect();

            const mPos = {
                x:evt.clientX - rc.left,
                y:evt.clientY - rc.top,
            };
            if (mPos.x < 0 || mPos.x > rc.width || mPos.y < 0 || mPos.y > rc.height) {
                document.body.style.cursor = 'auto';
                return;
            }

            document.body.style.cursor = `url(${TagPlaceCursor}) 32 32, pointer`;

            evt.preventDefault();
            evt.stopPropagation();

            const [ pitch, yaw ] = leftPannellumRef.current?.getViewer().mouseEventToCoords(evt);

            tagJig.current.yaw = yaw * Math.PI / 180;
            tagJig.current.pitch = pitch * Math.PI / 180;

            drawTagJig();
        };

        const onTagMouseWheel = (evt) => {

            const rc = tagCanvasContainerRef.current.getBoundingClientRect();

            const mPos = {
                x:evt.clientX - rc.left,
                y:evt.clientY - rc.top,
            };
            if (mPos.x < 0 || mPos.x > rc.width || mPos.y < 0 || mPos.y > rc.height) {
                return;
            }

            evt.stopPropagation();

            tagJig.current.orientation =
                tagJig.current.orientation
                + (evt.wheelDelta > 0 ? -1 : 1) * tagJig.current.orientationIncrement;

            drawTagJig();
        };

        const drawTagJig = () => {
            const viewInfo = {
                viewPitch:leftPannellumRef.current?.getViewer().getPitch(),
                viewYaw:leftPannellumRef.current?.getViewer().getYaw(),
                viewHfov:leftPannellumRef.current?.getViewer().getHfov(),
                canvasW:tagCanvasJigRef.current.width,
                canvasH:tagCanvasJigRef.current.height
            };

            const jigPath = createTagPath2DAt(
                tagJig.current.yaw,
                tagJig.current.pitch,
                tagJig.current.orientation,
                tagSize,
                viewInfo
            );

            const ctx = tagCanvasJigRef.current.getContext("2d");

            ctx.clearRect(0, 0, tagCanvasJigRef.current.width, tagCanvasJigRef.current.height);
            if (jigPath) {
                ctx.fillStyle = 'white';
                ctx.strokeStyle = 'black';
                ctx.fill(jigPath.path);
                ctx.stroke(jigPath.path);
            }
        };

        const clearTagJig = () => {
            const ctx = tagCanvasJigRef.current.getContext("2d");

            ctx.clearRect(0, 0, tagCanvasJigRef.current.width, tagCanvasJigRef.current.height);
        };

        window.addEventListener('keydown', onTagKeyDown);
        window.addEventListener('mousemove', onTagMouseMove);
        window.addEventListener('click', onTagMouseClick);
        window.addEventListener('wheel', onTagMouseWheel);

        tagCanvasContainerRef.current?.focus();

        return () => {
            document.body.style.cursor = 'auto';
            window.removeEventListener('keydown', onTagKeyDown);
            window.removeEventListener('mousemove', onTagMouseMove);
            window.removeEventListener('click', onTagMouseClick);
            window.removeEventListener('wheel', onTagMouseWheel);
        }

    }, [ tagAddState.step ]);


    useEffect(() => {

        if (tagEditState.step !== TAG_PICK_TARGET)
            return;

        const onTagKeyDown = (evt) => {
            const { key, altKey, ctrlKey, shiftKey } = evt;
            if (!altKey && !ctrlKey && !shiftKey) {
                if (key === 'Escape') {
                    setTagEditState(old => ({...old, step:TAG_INACTIVE, tagId:-1}));
                    return;
                }
            }
        };

        const onTagMouseClick = (evt) => {

            const rcC = tagCanvasContainerRef.current.getBoundingClientRect();
            const rcT = tagToolsRef.current?.getBoundingClientRect() ?? new DOMRect(0,0,0,0);

            const mPos = {
                x:evt.clientX - rcC.left,
                y:evt.clientY - rcC.top,
            };

            if (mPos.x >= rcT.left && mPos.x <= rcT.right &&
                mPos.y >= rcT.top && mPos.y <= rcT.bottom) {
                return;
            }
            if (mPos.x < rcC.left || mPos.x > rcC.right ||
                mPos.y < rcC.top || mPos.y > rcC.bottom) {
                return;
            }

            const canvas = tagCanvasRef.current;
            const ctx = canvas.getContext("2d");

            const checkMousePos = (entry) => ctx.isPointInPath(entry.path, mPos.x, mPos.y);
            const tagKey = xFindKey(tagRuntime.current.tagPathCache, checkMousePos);

            if (tagKey) {
                evt.preventDefault();
                evt.stopPropagation();

                keepalive();

                setTagEditState(old => ({...old, step:TAG_EDIT_TAG, tagId:1*tagKey}));
            }
            else {
                setTagEditState(old => ({ ...old, step:TAG_INACTIVE, tagId:-1 }));
            }
        };

        window.addEventListener('keydown', onTagKeyDown);
        window.addEventListener('click', onTagMouseClick);

        return () => {
            window.removeEventListener('keydown', onTagKeyDown);
            window.removeEventListener('click', onTagMouseClick);
        }

    }, [ tagEditState.step ]);


    useEffect(() => {

        if (tagLinkEditState.step !== TAG_PICK_TARGET)
            return;

        const onTagKeyDown = (evt) => {
            const { key, altKey, ctrlKey, shiftKey } = evt;
            if (!altKey && !ctrlKey && !shiftKey) {
                if (key === 'Escape') {
                    setTagLinkEditState(old => ({...old, step:TAG_INACTIVE, tagId:-1}));
                    return;
                }
            }
        };

        const onTagMouseClick = (evt) => {

            const rcC = tagCanvasContainerRef.current.getBoundingClientRect();
            const rcT = tagToolsRef.current?.getBoundingClientRect() ?? new DOMRect(0,0,0,0);

            const mPos = {
                x:evt.clientX - rcC.left,
                y:evt.clientY - rcC.top,
            };

            if (mPos.x >= rcT.left && mPos.x <= rcT.right &&
                mPos.y >= rcT.top && mPos.y <= rcT.bottom) {
                return;
            }
            if (mPos.x < rcC.left || mPos.x > rcC.right ||
                mPos.y < rcC.top || mPos.y > rcC.bottom) {
                return;
            }

            const canvas = tagCanvasRef.current;
            const ctx = canvas.getContext("2d");

            const checkMousePos = (entry) => ctx.isPointInPath(entry.path, mPos.x, mPos.y);
            const tagKey = xFindKey(tagRuntime.current.tagPathCache, checkMousePos);

            if (tagKey) {
                evt.preventDefault();
                evt.stopPropagation();

                keepalive();

                setTagLinkEditState(old => ({...old, step:TAG_EDIT_TAG, tagId:1*tagKey}));
            }
            else {
                setTagLinkEditState(old => ({ ...old, step:TAG_INACTIVE, tagId:-1 }));
            }
        };

        window.addEventListener('keydown', onTagKeyDown);
        window.addEventListener('click', onTagMouseClick);

        return () => {
            window.removeEventListener('keydown', onTagKeyDown);
            window.removeEventListener('click', onTagMouseClick);
        }

    }, [ tagLinkEditState.step ]);


    useEffect(() => {

        if (tagEraseState.step !== TAG_PICK_TARGET)
            return;

        const onTagKeyDown = (evt) => {
            const { key, altKey, ctrlKey, shiftKey } = evt;
            if (!altKey && !ctrlKey && !shiftKey) {
                if (key === 'Escape') {
                    setTagEraseState(old => ({...old, step:TAG_INACTIVE, tagId:-1}));
                    return;
                }
            }
        };

        const onTagMouseClick = (evt) => {

            const rcC = tagCanvasContainerRef.current.getBoundingClientRect();
            const rcT = tagToolsRef.current?.getBoundingClientRect() ?? new DOMRect(0,0,0,0);

            const mPos = {
                x:evt.clientX - rcC.left,
                y:evt.clientY - rcC.top,
            };

            if (mPos.x >= rcT.left && mPos.x <= rcT.right &&
                mPos.y >= rcT.top && mPos.y <= rcT.bottom) {
                return;
            }
            if (mPos.x < rcC.left || mPos.x > rcC.right ||
                mPos.y < rcC.top || mPos.y > rcC.bottom) {
                return;
            }

            const canvas = tagCanvasRef.current;
            const ctx = canvas.getContext("2d");

            const checkMousePos = (entry) => ctx.isPointInPath(entry.path, mPos.x, mPos.y);
            const tagKey = xFindKey(tagRuntime.current.tagPathCache, checkMousePos);

            if (tagKey) {
                evt.preventDefault();
                evt.stopPropagation();

                keepalive();

                setTagEraseState(old => ({...old, step:TAG_ASK_ERASE_TAG, tagId:1*tagKey}));
            }
            else {
                setTagEraseState(old => ({ ...old, step:TAG_INACTIVE, tagId:-1 }));
            }
        };

        window.addEventListener('keydown', onTagKeyDown);
        window.addEventListener('click', onTagMouseClick);

        return () => {
            window.removeEventListener('keydown', onTagKeyDown);
            window.removeEventListener('click', onTagMouseClick);
        }

    }, [ tagEraseState.step ]);


    const isTagAddPickTarget = tagAddState.step === TAG_PICK_TARGET;
    const isTagEditPickTarget = tagEditState.step === TAG_PICK_TARGET;
    const isTagLinkEditPickTarget = tagLinkEditState.step === TAG_PICK_TARGET;
    const isTagErasePickTarget = tagEraseState.step === TAG_PICK_TARGET;
    const isTagging = isTagAddPickTarget || isTagEditPickTarget || isTagErasePickTarget || isTagLinkEditPickTarget;

    const tagListTitle = (
        <div style={{
            display: 'flex',
            flexDirection: 'row',
            alignContent: 'center',
            width: '100%',
        }}>
            <span>Tags</span>
            <div style={{width:'100%'}}></div>
            <Button text={"Letöltés"} onClick={onDownloadTags}></Button>
        </div>);
    //</editor-fold>


    /*<editor-fold desc="Sidebar">*/
    const showProjectSelect = projectSelectVisible || selectedProject == null;
    const showSideBar = !showProjectSelect;

    const sideBarGeom = window.isTablet
        ? {
            width : 48,
            buttonSize : 32,
            iconSize : 24,
            padding : 8,
        }
        : {
            width : 48,
            buttonSize : 32,
            iconSize : 24,
            padding: 4,
        }
    ;
    /*</editor-fold desc="">*/


    /*<editor-fold desc="Planview">*/

    useEffect(() => {
        if (!selectedPlan)
            return;
        getProtectedResource(`/viewpoint.php?vplistof&did=${selectedPlan?.id}`)
            .then((resp) => {
                const vpTr = new DOMMatrix(selectedPlan.viewpoint_trafo);
                const vps = (selectedPlan.viewpoints || []).map(v => {
                    const vp = (new DOMPoint(v.x, v.y)).matrixTransform(vpTr);
                    vp.id = v.id;
                    vp.name = v.name;
                    vp.has_tags = v.has_tags ?? false;

                    const dbvp = resp.data.data.find(v => v.id === vp.id);
                    if (dbvp)
                        vp.has_tags = dbvp.has_tags ?? false;

                    return ViewPoint.make(vp);
                })

                setPlanDocument(old => ({
                    ...old,
                    pdfViewPoints:vps
                }));
            })
            .catch((err) => console.error(err))
        ;

    }, [ selectedPlan, setPlanDocument, reloadTags ]);

    let planViewStyle = {
        position: 'absolute',
        width : uiPlanView.width,
        height : uiPlanView.height
    }
    let planViewSizableCorner = -1;

    const SIDE_DIST = 16;
    const SIDE_DIST_RIGHT = 16;
    const SIDE_DIST_LEFT = 64;
    const SIDE_DIST_TOP = 64;

    if (uiPlanViewState === WindowState.MAXIMIZED) {
        planViewStyle = {
            position: 'absolute',
            left: SIDE_DIST,
            top: SIDE_DIST_TOP,
            right: SIDE_DIST,
            bottom: SIDE_DIST,
        }
    }
    else {
        if (uiPlanViewState === WindowState.MINIMIZED) {
            delete planViewStyle.height;
        }
        switch (uiPlanView.position)
        {
        // case Corner.NW:
        //     planViewStyle.left = SIDE_DIST_RIGHT;
        //     planViewStyle.top = SIDE_DIST_TOP;
        //     planViewSizableCorner = Corner.SE;
        //     break;
        // case Corner.NE:
        //     planViewStyle.right = SIDE_DIST;
        //     planViewStyle.top = SIDE_DIST_TOP;
        //     planViewSizableCorner = Corner.SW;
        //     break;
        case Corner.SE:
            planViewStyle.right = SIDE_DIST;
            planViewStyle.bottom = SIDE_DIST;
            planViewSizableCorner = Corner.NW;
            break;
        case Corner.SW:
        default:
            planViewStyle.left = SIDE_DIST_RIGHT;
            planViewStyle.bottom = SIDE_DIST;
            planViewSizableCorner = Corner.NE;
            break;
        }
    }

    const isPlanViewMinimized = uiPlanViewState === WindowState.MINIMIZED;
    const isPlanViewMaximized = uiPlanViewState === WindowState.MAXIMIZED;

    const hasLayers = planDocument && planDocument.pdfGroups.length > 0;

    if (isTagAddPickTarget)
    {
        planViewStyle.visibility = 'hidden';
    }

    /*</editor-fold desc="">*/


    //<editor-fold desc="Viewer">
    const hasModel = selectedPlan?.a360_hub?.length > 0 &&
        selectedPlan?.a360_file?.length > 0 &&
        selectedPlan?.a360_project?.length > 0;

    const showDocumentView = panoDocument.viewPointDocuments.length > 0;
    const singleDocument = hasModel ? panoDocument.viewPointDocuments.length <= 2 : panoDocument.viewPointDocuments.length === 1;
    const showSplitButton = !singleDocument && !splitView;
    const leftDocumentIndex = panoDocument.viewPointDocuments.findIndex(i => i.id === panoDocument.leftDocumentId);

    const leftSelectedItem = panoDocument.viewPointDocuments.find(i => i.id === panoDocument.leftDocumentId);
    const leftSelectedItemText = leftSelectedItem?.upload_at ?? leftSelectedItem?.name;

    const leftFirstDocument = leftDocumentIndex === panoDocument.viewPointDocuments.length-1;
    const leftLastDocument = leftDocumentIndex === 0;

    const leftRenderSelectItem = useCallback((item, status) => {
        if (item.type === 'model/gltf-binary')
        {
            return <MenuItem
                key={-13}
                text={'Modell'}
                selected={item.id === panoDocument.leftDocumentId}
                onClick={status.handleClick}
            />;
        }
        return <MenuItem
            key={item.id}
            text={item.upload_at}
            selected={item.id === panoDocument.leftDocumentId}
            onClick={status.handleClick}
        />;
    }, [panoDocument]);


    const onLeftSelectItem = useCallback((item) => {
        setPanoDocument(old => ({
            ...old,
            leftDocumentId:item.id,
        }));
    }, [setPanoDocument]);


    const onLeftPreviousDocumentClick = useCallback(() => {
        let i = panoDocument.viewPointDocuments.findIndex(i => i.id === panoDocument.leftDocumentId);
        if (i < panoDocument.viewPointDocuments.length-1)
        {
            setPanoDocument(old => ({
                ...old,
                leftDocumentId:panoDocument.viewPointDocuments[i+1].id,
            }));
        }
    }, [panoDocument]);


    const onLeftNextDocumentClick = useCallback(() => {
        let i = panoDocument.viewPointDocuments.findIndex(i => i.id === panoDocument.leftDocumentId);
        if (i > 0)
        {
            setPanoDocument(old => ({
                ...old,
                leftDocumentId:panoDocument.viewPointDocuments[i-1].id,
            }));
        }
    }, [panoDocument]);


    const rightDocumentIndex = panoDocument.viewPointDocuments.findIndex(i => i.id === panoDocument.rightDocumentId);
    const rightSelectedItemText = panoDocument.viewPointDocuments.find(i => i.id === panoDocument.rightDocumentId)?.upload_at;
    const rightFirstDocument = rightDocumentIndex === panoDocument.viewPointDocuments.length-1;
    const rightLastDocument = rightDocumentIndex === 0;

    const rightRenderSelectItem = useCallback((item, status) => {
        return <MenuItem
            key={item.id}
            text={item.upload_at}
            selected={item.id === panoDocument.rightDocumentId}
            onClick={status.handleClick}
        />;
    }, [panoDocument]);


    const onRightSelectItem = useCallback((item) => {
        setPanoDocument(old => ({
            ...old,
            rightDocumentId:item.id,
        }));
    }, [setPanoDocument]);


    const onRightPreviousDocumentClick = useCallback(() => {
        let i = panoDocument.viewPointDocuments.findIndex(i => i.id === panoDocument.rightDocumentId);
        if (i < panoDocument.viewPointDocuments.length-1)
        {
            setPanoDocument(old => ({
                ...old,
                rightDocumentId:panoDocument.viewPointDocuments[i+1].id,
            }));
        }
    }, [panoDocument]);


    const onRightNextDocumentClick = useCallback(() => {
        let i = panoDocument.viewPointDocuments.findIndex(i => i.id === panoDocument.rightDocumentId);
        if (i > 0)
        {
            setPanoDocument(old => ({
                ...old,
                rightDocumentId:panoDocument.viewPointDocuments[i-1].id,
            }));
        }
    }, [panoDocument]);


    const onSplitViewClick = useCallback(() => {
        dispatch(setSplitView(true));
    }, []);


    const onCloseSplitViewClick = useCallback(() => {
        dispatch(setSplitView(false));
    }, []);

    //</editor-fold>


    //<editor-fold desc="Document views">

    let documentView;
    let selectorDock = (uiPlanView.position === Corner.SW || uiPlanView.position === Corner.SE)
        ? 'top'
        : 'bottom'
    ;

    if (splitView)
    {
        let dview;

        if (leftDocument.id === MODEL_ID)
        {
            dview = (
                <AutodeskModelView id={"adsk_split"}
                                   visible={true}
                                   a360_hub={selectedPlan.a360_hub}
                                   a360_project={selectedPlan.a360_project}
                                   a360_file={selectedPlan.a360_file}
                />
            );
        }
        else {
            dview =(<SingleDocumentView
                visible={showDocumentView}
                ref={leftPannellumRef}
                image={leftDocument.url}
                pitch={leftPitchRef.current}
                yaw={leftYawRef.current}
                onRender={() => onLeftPanoRender(leftTagsRef.current)}
                onLoad={() => propagateHotSpots(leftPannellumRef.current, panoDocument.hotSpots)}
            />);
        }

        documentView = (
            <div style={{
                position: 'relative',
                width:'100%',
                height:'100%',
                display:'flex',
                flexDirection:'row',
                backgroundColor: Colors.DARK_GRAY5,
            }}>
                <div style={{
                    position: 'relative',
                    width:'100%',
                    height:'100%',
                    display:'flex',
                    flexDirection:'row',
                    marginRight:1,
                }}>
                    {dview}
                    { !isPlanViewMaximized &&
                    <SelectorContainer
                        visible={showDocumentView}
                        justifyContent={'right'}
                        dock={selectorDock}
                    >
                        <ButtonGroup>
                            <Button
                                icon={IconNames.CARET_LEFT}
                                disabled={leftFirstDocument}
                                onClick={onLeftPreviousDocumentClick}
                            />
                            <Select
                                items={panoDocument.viewPointDocuments}
                                itemRenderer={leftRenderSelectItem}
                                filterable={false}
                                onItemSelect={onLeftSelectItem}
                            >
                                <Button
                                    rightIcon={IconNames.CARET_DOWN}
                                    text={leftSelectedItemText}
                                />
                            </Select>
                            <Button
                                icon={IconNames.CARET_RIGHT}
                                disabled={leftLastDocument}
                                onClick={onLeftNextDocumentClick}
                            />
                        </ButtonGroup>
                    </SelectorContainer>
                    }
                </div>
                <div style={{
                    position: 'relative',
                    width:'100%',
                    height:'100%',
                    display:'flex',
                    flexDirection:'row',
                    marginLeft:1,
                }}>
                    <SingleDocumentView
                        visible={showDocumentView}
                        ref={rightPannellumRef}
                        image={rightDocument.url}
                        pitch={leftPitchRef.current}
                        yaw={leftYawRef.current}
                        onRender={onRightPanoRender}
                        onLoad={() => propagateHotSpots(rightPannellumRef.current, panoDocument.hotSpots)}
                    />
                    <SelectorContainer
                        visible={showDocumentView}
                        justifyContent={'left'}
                        dock={selectorDock}
                    >
                        <ButtonGroup>
                            <Button
                                icon={IconNames.DELETE}
                                onClick={onCloseSplitViewClick}
                            />
                            <Button
                                icon={IconNames.CARET_LEFT}
                                disabled={rightFirstDocument}
                                onClick={onRightPreviousDocumentClick}
                            />
                            <Select
                                items={panoDocument.viewPointDocuments}
                                itemRenderer={rightRenderSelectItem}
                                filterable={false}
                                onItemSelect={onRightSelectItem}
                            >
                                <Button
                                    rightIcon={IconNames.CARET_DOWN}
                                    text={rightSelectedItemText}
                                />
                            </Select>
                            <Button
                                icon={IconNames.CARET_RIGHT}
                                disabled={rightLastDocument}
                                onClick={onRightNextDocumentClick}
                            />
                        </ButtonGroup>
                    </SelectorContainer>
                </div>
            </div>
        );
    }
    else
    {
        let dview;

        if (leftDocument.id === MODEL_ID)
        {
            dview = (
                    <AutodeskModelView id={"adsk_single"}
                                       visible={true}
                                       a360_hub={selectedPlan.a360_hub}
                                       a360_project={selectedPlan.a360_project}
                                       a360_file={selectedPlan.a360_file}
                                       setYaw={onSetYaw}
                                       setPitch={onSetPitch}
                    />
                );
        }
        else {
            dview =(<SingleDocumentView
                visible={showDocumentView}
                ref={leftPannellumRef}
                image={leftDocument.url}
                pitch={leftPitchRef.current}
                yaw={leftYawRef.current}
                onRender={() => onLeftPanoRender(leftTagsRef.current)}
                onLoad={() => propagateHotSpots(leftPannellumRef.current, panoDocument.hotSpots)}
                onMouseDown={suspendTagMouseEventHandling}
                onMouseUp={restoreTagMouseEventHandling}
            />);
        }

        documentView = (
            <div style={{
                position: 'relative',
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
            }}>
                {dview}
                {!isPlanViewMaximized &&
                    <SelectorContainer
                        visible={showDocumentView}
                        justifyContent={'center'}
                        dock={selectorDock}
                    >
                        <ButtonGroup large={window.isTablet ?? false}>
                            <Button
                                icon={IconNames.CARET_LEFT}
                                disabled={leftFirstDocument}
                                onClick={onLeftPreviousDocumentClick}
                            />
                            <Select
                                items={panoDocument.viewPointDocuments}
                                itemRenderer={leftRenderSelectItem}
                                filterable={false}
                                onItemSelect={onLeftSelectItem}
                            >
                                <Button
                                    rightIcon={IconNames.CARET_DOWN}
                                    text={leftSelectedItemText}
                                />
                            </Select>
                            <Button
                                icon={IconNames.CARET_RIGHT}
                                disabled={leftLastDocument}
                                onClick={onLeftNextDocumentClick}
                            />
                            {showSplitButton &&
                                <Button
                                    icon={IconNames.SPLIT_COLUMNS}
                                    onClick={onSplitViewClick}
                                />
                            }
                        </ButtonGroup>
                    </SelectorContainer>
                }
                {/*<editor-fold desc="Tag Canvas">*/}
                <div
                    ref={tagCanvasContainerRef}
                    id={'tagCanvasContainer'}
                    style={{
                        position: 'absolute',
                        // backgroundColor:'rgba(0,255,0,0.2)',
                        left: 0, top: 0, right: 0, bottom: 0,
                        zIndex: 19,
                        pointerEvents: isTagAddPickTarget ? 'all' : 'none',
                        overflow: 'hidden',
                    }}
                >
                    <canvas
                        ref={tagCanvasRef}
                        width={0}
                        height={0}
                        style={{position: 'absolute', left: 0, top: 0, touchAction: 'none'}}
                    >
                    </canvas>
                    <canvas
                        ref={tagCanvasJigRef}
                        width={0}
                        height={0}
                        style={{position: 'absolute', left: 0, top: 0, touchAction: 'none'}}
                    >
                    </canvas>
                </div>
                {/*</editor-fold>*/}
            </div>
        );
    }

    //</editor-fold>

    const d = (selectedPlan?.direction || 0) * 180.0 / Math.PI;

    //let frustumYaw = 360 - (leftYawRef.current + d - 90);
    let frustumYaw = (leftYawRef.current + d - 90);
    while (frustumYaw >= 360) frustumYaw -= 360;
    while (frustumYaw < 0) frustumYaw += 360;

    const isTagEditEnabled = (user?.administrator || user?.projectmanager || user?.customer);
    const isTagEraseEnabled = (user?.administrator || user?.projectmanager);
    const isTagAvailable = !splitView && panoDocument.leftDocumentId > 0;

    const showAddTag = tagAddState.step === TAG_EDIT_TAG;
    const showEditTag = tagEditState.step === TAG_EDIT_TAG;
    const showEraseTag = tagEraseState.step === TAG_ASK_ERASE_TAG;

    const showEditTagLink = tagLinkEditState.step === TAG_EDIT_TAG;

    let a360Credentials = {
        client_id:'',
        client_secret:'',
        redirect_uri:'',
    };
    //if (__BIMLENS__) {
        if (__DEVELOPMENT__)
            a360Credentials = {
                client_id:'TJ7Lel8mBXnIxRAG6PlJ5Z9Kl0P9VDmy',
                client_secret:'Qapi5llW1ABMIuAI',
                redirect_uri:encodeURI("https://192.168.1.110:9000/a360callback.html")
            };
        else
            a360Credentials = {
                // client_id:'AuPG1SyK3Smxu7zG21v5pNhYujolLr7y',
                // client_secret:'wBES0xdFpHziMsuz',
                // redirect_uri:encodeURI("https://bimlens.logickft.hu/manager/a360callback.html")
                client_id:'fxVr3CtimaWAn3VBGPs9MKqhYjarrD4j',
                client_secret:'FTIqJHABbFLOGr4F',
                redirect_uri:encodeURI("https://mfgt.logickft.hu/viewer/a360callback.html")
            };
    //} else {
    //
    //}

    return (
        <A360Provider a360Credentials={a360Credentials}>
            {/*<editor-fold desc="Sidebar">*/}
            {showSideBar && false &&
                <SideBar darkMode={uiDarkMode} width={`${sideBarGeom.width}px`} style={{
                    position:'absolute',
                    top:0, left:0, bottom:0,
                    padding:sideBarGeom.padding,
                    zIndex:300
                }}>
                    <SideBarButton darkMode={uiDarkMode} style={{
                        width: sideBarGeom.buttonSize,
                        height: sideBarGeom.buttonSize,
                        color:'rgba(0,0,0,0.5)'
                    }}>
                        <Tooltip2 content={'Select project'} >
                            <BiGridAlt
                                // icon={IconNames.MENU}
                                size={sideBarGeom.iconSize}
                                onClick={() => dispatch(setProjectSelectVisible(true))}
                            />
                        </Tooltip2>
                    </SideBarButton>
                    <div style={{ height:'100%' }}/>
                    <DarkModeSwitch large={false} />
                    <div style={{ height:4 }}/>
                </SideBar>
            }
            {/*</editor-fold>*/}


            {/*<editor-fold desc="Project selector">*/}
            <Drawer canOutsideClickClose={false}
                    size={DrawerSize.STANDARD}
                    isOpen={showProjectSelect}
                    title={'Projektek'}
                    position={Position.LEFT}
                    hasBackdrop
                    onClose={() => dispatch(setProjectSelectVisible(false))}
            >
                <ProjectSelect />
            </Drawer>
            {/*</editor-fold>*/}


            {/*<editor-fold desc="PlanView">*/}
            <PlanViewFrame
                sizableCorner={planViewSizableCorner}
                style={{ ...planViewStyle }}
            >
                <PlanViewAdvanced2
                    ref={planViewRef}
                    zIndexBase={14}
                    width={'100%'}
                    height={'100%'}
                    backgroundColor={ uiDarkMode ? Colors.GRAY3 : Colors.GRAY2}
                    shadowColor={ uiDarkMode ? Colors.GRAY1 : Colors.DARK_GRAY4}
                    pdfPage={planDocument.pdfPage}
                    pdfPageViewport={planDocument.pdfPageViewport}
                    pdfOptionalContentConfig={planDocument.pdfOptionalContentConfig}
                    pdfVisibleGroups={planVisibleLayers}
                >
                    <PlanViewAdvanced2DrawViewPoints
                        ref={planViewViewPointDrawerRef}
                        planView={planViewRef.current}
                        viewPoints={planDocument.pdfViewPoints}
                        zIndexBase={17}
                    />
                    <PlanViewAdvanced2ViewPointSelect
                        ref={planViewViewPointSelectRef}
                        planView={planViewRef.current}
                        viewPoints={planDocument.pdfViewPoints}
                        zIndexBase={18}
                        selectedViewpointId={selectedViewpointId}
                        yaw={frustumYaw}
                        hfov={leftHfov}
                    />
                </PlanViewAdvanced2>
                {!isPlanViewMinimized &&
                    <div style={{
                        position:'absolute',
                        left:8,
                        top:44,
                        bottom:8,
                        display:'flex',
                        flexDirection:'column',
                        zIndex: 19
                    }}>
                        <Tooltip2 content={'Teljes nézet'} disabled={window.isTablet}>
                            <Button icon={IconNames.CLIP}
                                    style={{marginBottom:4}}
                                    onClick={onZoomExtents}
                            />
                        </Tooltip2>
                        {hasLayers &&
                            <MultiSelectPopup items={planDocument.pdfGroups}
                                              itemsInitState={planVisibleLayers}
                                              darkMode={uiDarkMode}
                            />
                        }
                    </div>
                }
            </PlanViewFrame>
            {/*</editor-fold desc="">*/}


            {/*<editor-fold desc="Main view">*/}
            <div id={'www'} style={{
                position: 'absolute',
                left:0, top:0, right:0, bottom:0,
                display: 'flex',
                flexDirection: 'column'
            }}>
                <Navbar>
                    <div style={{width:'100%'}}>
                        <NavbarGroup align={Alignment.LEFT} >
                            <Button
                                className={Classes.MINIMAL}
                                icon={<BiGridAlt size={sideBarGeom.iconSize} />}
                                disabled={isTagging}
                                onClick={() => dispatch(setProjectSelectVisible(true))}
                            />
                            <NavbarDivider />
                            <NavbarHeading>{selectedProject?.name}</NavbarHeading>
                        </NavbarGroup>
                        <NavbarGroup align={Alignment.RIGHT}>
                            <DarkModeSwitch large={false} />
                        </NavbarGroup>
                    </div>
                </Navbar>
                <div style={{
                    position: 'relative',
                    width: '100%', height: '100%',
                }}>
                    {documentView}
                    {isTagAvailable && !isPlanViewMaximized &&
                        <div ref={tagToolsRef}
                             style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 4,
                                position:'absolute',
                                left:8, top:8,
                                padding:4,
                                backgroundColor:Colors.LIGHT_GRAY5,
                                border: `2px solid ${Colors.LIGHT_GRAY2}`,
                                borderRadius:3,
                                zIndex:19
                            }}
                        >
                            <Button
                                className={Classes.MINIMAL}
                                icon={<ListTagsIcon size={IconSize.LARGE}/>}
                                disabled={!isTagAvailable || isTagging}
                                onClick={onShowPlanTags}
                            />
                            { isTagEditEnabled &&
                                <Button
                                    className={Classes.MINIMAL}
                                    icon={<AddTagIcon size={IconSize.LARGE}/>}
                                    disabled={!isTagAvailable || isTagging}
                                    onClick={onTagAddStart}
                                />
                            }
                            { isTagEditEnabled &&
                                <Button
                                    className={Classes.MINIMAL}
                                    icon={<EditTagIcon size={IconSize.LARGE} />}
                                    disabled={!isTagAvailable || (isTagging && !isTagEditPickTarget)}
                                    intent={isTagEditPickTarget ? Intent.SUCCESS : Intent.NONE}
                                    onClick={onTagEditStart}
                                />
                            }
                            { isTagEditEnabled &&
                                <Button
                                    className={Classes.MINIMAL}
                                    icon={<EditTagAttachmentsIcon size={IconSize.LARGE} />}
                                    disabled={!isTagAvailable || (isTagging && !isTagLinkEditPickTarget)}
                                    intent={isTagLinkEditPickTarget ? Intent.SUCCESS : Intent.NONE}
                                    onClick={onTagLinkEditStart}
                                />
                            }
                            { isTagEraseEnabled &&
                                <Button
                                    className={Classes.MINIMAL}
                                    icon={<DeleteTagIcon size={IconSize.LARGE}/>}
                                    disabled={!isTagAvailable || (isTagging && !isTagErasePickTarget)}
                                    intent={isTagErasePickTarget ? Intent.DANGER : Intent.NONE}
                                    onClick={onTagEraseStart}
                                />
                            }
                            <Button
                                //className={Classes.MINIMAL}
                                icon={<TagsLegendIcon size={IconSize.LARGE}/>}
                                disabled={!isTagAvailable || isTagging}
                                intent={tagsLegendVisible ? Intent.PRIMARY : Intent.NONE}
                                onClick={() => dispatch(setTagsLegendVisible(!tagsLegendVisible))}
                            />
                        </div>
                    }
                    {isTagAvailable && tagsLegendVisible &&
                        <TagsLegendContainer>
                            <span className={'bp4-text-large'} style={{padding:'8px 0'}}><b>Categories</b></span>
                            {tagSettings.categories.map(cat => (
                                <span key={cat.id} style={{backgroundColor:cat.bgcolor, borderRadius:2, padding:2}}>{cat.category}</span>
                            ))}
                            <span className={'bp4-text-large'} style={{padding:'8px 0'}}><b>Disciplines</b></span>
                            {tagSettings.disciplines.map(dsc => (
                                <span key={dsc.id} style={{backgroundColor:dsc.bgcolor, borderRadius:2, padding:2}}>{dsc.discipline}</span>
                            ))}
                        </TagsLegendContainer>
                    }
                </div>
            </div>
            {/*</editor-fold>*/}


            {/*<editor-fold desc="Plan tags">*/}
            <Drawer canOutsideClickClose={false}
                    canEscapeKeyClose={!showTagHistoryPhoto.show}
                    size={DrawerSize.SMALL}
                    isOpen={showPlanTagList.show}
                    title={tagListTitle}
                    position={Position.BOTTOM}
                    hasBackdrop={true}
                    onClose={() => setShowPlanTagList(old => ({...old, show:false}))}
            >
                <TagList projectId={selectedProject?.id}
                         onJumpToTag={onJumpToTag}
                         onShowHistoryImage={onShowHistoryImage}
                />
            </Drawer>
            {/*</editor-fold>*/}


            {showAddTag &&
                <EditTagDialog
                    isOpen={tagAddState.step === TAG_EDIT_TAG}
                    projectId={selectedProject?.id ?? 0}
                    documentId={panoDocument.leftDocumentId}
                    mode={EditTagMode.ADD}
                    yaw={tagJig.current.yaw}
                    pitch={tagJig.current.pitch}
                    orientation={tagJig.current.orientation}
                    onClose={onTagEditCloseAddTag}
                />
            }
            {showEditTag &&
                <EditTagDialog
                    isOpen={showEditTag}
                    projectId={selectedProject?.id ?? 0}
                    documentId={panoDocument.leftDocumentId}
                    tagId={tagEditState.tagId}
                    mode={EditTagMode.EDIT}
                    onClose={onTagEditCloseEditTag}
                />
            }
            {showEraseTag &&
                <AskAndEraseTagDialog
                    isOpen={showEraseTag}
                    projectId={selectedProject?.id ?? 0}
                    documentId={panoDocument.leftDocumentId}
                    tagId={tagEraseState.tagId}
                    onClose={onTagEditCloseEraseTag}
                />
            }
            {showEditTagLink &&
                <EditTagLinkDialog
                    isOpen={showEditTagLink}
                    projectId={selectedProject?.id ?? 0}
                    documentId={panoDocument.leftDocumentId}
                    tagId={tagLinkEditState.tagId}
                    mode={EditTagMode.EDIT}
                    onClose={onTagLinkEditCloseEditTagLink}
                />
            }
            {showTagHistory.show &&
                <ShowTagHistoryDialog
                    isOpen={showTagHistory.show}
                    tagId={showTagHistory.id}
                    onClose={() => setShowTagHistory({id:-1,show:false})}
                />
            }


            {showTagHistoryPhoto.show &&
                <Dialog
                    title={`Címke:`}
                    canEscapeKeyClose
                    canOutsideClickClose
                    isOpen={showTagHistoryPhoto.show}
                    onClose={() => { URL.revokeObjectURL(showTagHistoryPhoto.url); setShowTagHistoryPhoto({show:false, url:null}); }}
                    style={{
                        width:'90vw',
                        height:'90vh',
                        zIndex:150,
                    }}
                >
                    <div style={{
                        display:'absolute',
                        flexDirection:'column',
                        padding:'1rem',
                        backgroundImage: `url(${showTagHistoryPhoto.url})`,
                        backgroundRepeat: `no-repeat`,
                        backgroundPosition: `center`,
                        backgroundSize: `contain`,
                        width:'100%',
                        height:'100%',
                    }}>
                    </div>
                </Dialog>
            }

            {/*{__DEVELOPMENT__ &&*/}
            {/*    <div style={{zIndex:1000, backgroundColor:'lightsalmon'}}>*/}
            {/*        <span>selected project: {selectedProject?.name}</span>*/}
            {/*        <div>mainpage render count: {renderCnt.current++}</div>*/}
            {/*    </div>*/}
            {/*}*/}
        </A360Provider>
    );
}
/*
 category: "Panorama photo"
 category_id: 2
 direction: null
 id: 66
 name: "FF_02_20220216.jpg"
 navigation_radius: null
 original_name: "FF_02_20220216.jpg"
 page_index: null
 project_id: "1"
 type: "image/jpeg"
 upload_at: "2022-02-16"
 viewpoint: "FF_02"
 viewpoint_id: 11
 viewpoint_trafo: null
*/